/**
 * MSAL silent token renewal frame guard.
 *
 * When the refresh token is no longer usable (about a day after sign-in),
 * MSAL renews tokens by loading the redirect URI (the site origin) in a hidden
 * iframe and reading the Microsoft answer (`#code=…&state=…`) from that
 * frame's URL. If the CRE app boots inside that frame it consumes the answer
 * (App.js → completeCreMsalLogin) and navigates away, so the waiting page
 * finds an empty URL: `hash_empty_error`. That was the race behind
 * "Unable to connect MedMic" (production, 22 Oct 2026).
 *
 * In such a frame the app must not start: the URL is left untouched for MSAL.
 * Normal embeds of CRE in host iframes (`?token=…`) never carry an MSAL
 * answer (code/error together with state), so they are not affected.
 */

/** @param {Window} [win] */
export function isInFrame(win = typeof window !== "undefined" ? window : undefined) {
  if (!win) return false;
  try {
    return win.self !== win.top;
  } catch {
    return true; // cross-origin parent
  }
}

/** URL carries an OAuth/MSAL answer: state plus code or error, in hash or query. */
export function hasMsalResponseParams(loc) {
  if (!loc) return false;
  const s = `${loc.hash || ""}&${loc.search || ""}`;
  const hasState = /(?:^|[#&?])state=/.test(s);
  const hasResult = /(?:^|[#&?])(?:code|error)=/.test(s);
  return hasState && hasResult;
}

/** True when this page is MSAL's hidden renewal frame holding the answer. */
export function isMsalSilentFrameResponse(win = typeof window !== "undefined" ? window : undefined) {
  if (!win) return false;
  return isInFrame(win) && hasMsalResponseParams(win.location);
}
